import { MerchantService } from '@galaxy/billing';
import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import {
  App,
  AppKey,
  AppPrices,
  AppPricingApiService,
  FieldMask,
  GetMultiAppRequest,
  GetMultiPricingRequest,
  PartnerApiService,
  RequestedApp,
} from '@vendasta/marketplace-apps';
import { MarketplacePackagesApiService, Package } from '@vendasta/marketplace-packages';
import { uniq } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  map,
  Observable,
  of,
  skipWhile,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { InventoryItemWithPricing } from '../inventory-item';
import { parseRetailItemPrice } from '../item-price/retail';
import { parseWholesaleItemPrice } from '../item-price/wholesale';
import { ItemRow, RETAIL_PRICING_CONTEXT, WHOLESALE_PRICING_CONTEXT } from './interface';
import { parseItemFees } from './utils';

export class ItemPricingTableDataSource implements PaginatedAPIInterface<ItemRow> {
  private refreshTrigger$$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  private appCache: Map<string, App> = new Map<string, App>();
  private packageCache: Map<string, Package> = new Map<string, Package>();
  private appPricesCache: Map<string, AppPrices> = new Map<string, AppPrices>();

  constructor(
    private readonly items$: Observable<InventoryItemWithPricing[]>,
    private readonly partnerId: string,
    private readonly marketId: string,
    private readonly businessId: string,
    private readonly excludeWholesale: boolean,
    private readonly appPricingApiService: AppPricingApiService,
    private readonly partnerApiService: PartnerApiService,
    private readonly merchantService: MerchantService,
    private readonly marketplacePackagesApiService: MarketplacePackagesApiService,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  get(req: PagedListRequestInterface): Observable<PagedResponseInterface<ItemRow>> {
    const rows$ = this.items$.pipe(
      skipWhile((items) => !items?.length),
      map((items) => {
        const pricesToFetch = uniq(
          items.filter((item) => !this.appPricesCache.has(item.itemId) || !this.excludeWholesale),
        );
        const appsToFetch = uniq(
          items.filter((item) => item.type !== 'package' && !this.appCache.has(InventoryItemToCacheKey(item))),
        );
        const packagesToFetch = uniq(
          items.filter((item) => item.type === 'package' && !this.packageCache.has(item.itemId)),
        );
        return [items, pricesToFetch, appsToFetch, packagesToFetch];
      }),
      switchMap(([items, pricesToFetch, appsToFetch, packagesToFetch]) => {
        const appPrices$ = this.fetchAppPrices(pricesToFetch);
        const apps$ = this.fetchApps(appsToFetch);
        const packages$ = this.fetchPackages(packagesToFetch);
        return forkJoin([of(items), appPrices$, apps$, packages$]);
      }),
      map(([items, prices, apps, packages]) => {
        this.appPricesCache = new Map([...this.appPricesCache, ...prices]);
        this.appCache = new Map([...this.appCache, ...apps]);
        this.packageCache = new Map([...this.packageCache, ...packages]);
        return { items, prices: this.appPricesCache, apps: this.appCache, packages: this.packageCache };
      }),
      withLatestFrom(this.getMarketCurrency$()),
      map(([{ items, prices, apps, packages }, marketCurrency]) => {
        const rows = this.buildRows(items, prices, apps, packages, marketCurrency);
        return {
          data: rows,
          pagingMetadata: {
            nextCursor: '',
            hasMore: false,
          },
        } as PagedResponseInterface<ItemRow>;
      }),
    );

    return combineLatest([rows$, this.refreshTrigger$$]).pipe(
      map(([rows]) => {
        rows.data = rows.data.filter((row) => row.quantity > 0);
        return rows;
      }),
      startWith({ data: [], pagingMetadata: { nextCursor: '', hasMore: false } }),
    );
  }

  private buildRows(
    items: InventoryItemWithPricing[],
    itemPrices: Map<string, AppPrices>,
    apps: Map<string, App>,
    packages: Map<string, Package>,
    marketCurrency: string,
  ): ItemRow[] {
    return items.map((item, index) => {
      const itemPrice = itemPrices.get(item.itemId) as AppPrices;
      const app = apps.get(InventoryItemToCacheKey(item)) as App;
      const pkg = packages.get(item.itemId) as Package;
      return this.buildItemRow(item, itemPrice, app, pkg, marketCurrency, index);
    });
  }

  private fetchAppPrices(items: InventoryItemWithPricing[]): Observable<Map<string, AppPrices>> {
    if (!items.length) return of(new Map<string, AppPrices>());
    const getMultiPricingRequest = new GetMultiPricingRequest({
      appIds: items.map((item) => item.itemId),
      pricingContexts: {
        paths: this.getPricingContexts(),
      },
      partnerId: this.partnerId,
      marketId: this.marketId,
      businessId: this.businessId,
    });
    if (!this.excludeWholesale) {
      getMultiPricingRequest.filterOptions = new FieldMask({ paths: ['instance_price'] });
      getMultiPricingRequest.requestedItems = items.map((item) => {
        return new RequestedApp({
          appId: item.itemId,
          quantity: item.pricing?.quantity || item.quantity,
        });
      });
    }
    return this.appPricingApiService
      .getMultiPricing(getMultiPricingRequest)
      .pipe(map((response) => this.mapify(response.appPrices, (ap: AppPrices) => ap.appId)));
  }

  private getPricingContexts(): string[] {
    const pricingContexts: string[] = [];
    if (!this.excludeWholesale) pricingContexts.push(WHOLESALE_PRICING_CONTEXT);
    pricingContexts.push(RETAIL_PRICING_CONTEXT);
    return pricingContexts;
  }

  private fetchApps(items: InventoryItemWithPricing[]): Observable<Map<string, App>> {
    const appItems = items.filter((item) => item.type !== 'package');
    if (!appItems.length) return of(new Map<string, App>());

    const getMultiAppRequest = new GetMultiAppRequest({
      projectionFilter: {
        paths: ['sharedMarketingInformation'],
      },
      appKeys: appItems.map((item) => {
        return new AppKey({
          appId: item.itemId,
          editionId: item.type === 'app' ? item.edition?.editionId : undefined,
        });
      }),
      partnerId: this.partnerId,
      marketId: this.marketId,
      includeNotEnabled: true,
    });

    return this.partnerApiService
      .getMultiApp(getMultiAppRequest)
      .pipe(map((response) => this.mapify(response.apps, (a: App) => AppToCacheKey(a))));
  }

  private fetchPackages(items: InventoryItemWithPricing[]): Observable<Map<string, Package>> {
    const packageIds = items.filter((item) => item.type === 'package').map((item) => item.itemId);
    if (!packageIds.length) return of(new Map<string, Package>());
    return this.marketplacePackagesApiService.getMultiPackages({ packageIds: packageIds }).pipe(
      map((resp) => {
        const packages = resp.packageContainers.map((container) => container.package);
        return this.mapify(packages, (p: Package) => p.packageId);
      }),
    );
  }

  private getMarketCurrency$() {
    return this.merchantService.getMultiRetailConfigurations(this.partnerId, [this.marketId]).pipe(
      take(1),
      map((resp) => {
        let currencyCode = '';
        resp.forEach((retailConfig) => {
          if (retailConfig?.currencyCode) {
            currencyCode = retailConfig.currencyCode;
          }
        });
        return currencyCode;
      }),
    );
  }

  private buildItemRow(
    item: InventoryItemWithPricing,
    appPrices: AppPrices,
    app: App,
    marketplacePackage: Package,
    marketCurrency: string,
    index: number,
  ): ItemRow {
    if (item?.pricing?.quantity) {
      item.quantity = item.pricing.quantity;
    }
    const itemRow = {
      ...item,
      name: item.type !== 'package' ? app?.sharedMarketingInformation?.name : marketplacePackage?.name,
      iconUrl: item.type !== 'package' ? app?.sharedMarketingInformation?.iconUrl : marketplacePackage?.icon,
      wholesalePricing: parseWholesaleItemPrice({
        appPrices,
        item,
      }),
      retailPricing: parseRetailItemPrice({
        appPrices,
        item,
        marketCurrency,
      }),
      defaultRetailPricing: parseRetailItemPrice({
        appPrices,
        item,
        marketCurrency,
        useDefault: true,
      }),
      fees: parseItemFees(appPrices, item),
      isMultiactivatable: app?.activationInformation?.multipleActivationsEnabled || false,
      index: index,
    } as ItemRow;
    if (itemRow.type === 'app' && itemRow.edition) {
      itemRow.edition.name = app?.sharedMarketingInformation?.editionName;
      if (app?.editionInformation?.editions?.length > 0 && app?.editionInformation?.usesEditions) {
        itemRow.editions = [];
        app.editionInformation.editions.forEach((item) => {
          itemRow.editions.push({ name: item.name, editionId: item.appKey?.editionId });
        });
      }
    }
    if (item.pricing?.billingPeriod) {
      itemRow.billingTerm = {
        startDate: item.pricing.billingPeriod.startDate,
        endDate: item.pricing.billingPeriod.endDate,
        duration: item.pricing.billingPeriod.duration,
      };
    }

    if (
      itemRow.retailPricing.price < itemRow.defaultRetailPricing.price &&
      itemRow.retailPricing.frequency === itemRow.defaultRetailPricing.frequency &&
      itemRow.retailPricing.currency === itemRow.defaultRetailPricing.currency &&
      !itemRow.wholesalePricing.usesVariablePricing
    ) {
      itemRow.isDiscountedRetailPrice = true;
    }

    if (
      itemRow.retailPricing.price !== itemRow.defaultRetailPricing.price ||
      itemRow.retailPricing.frequency !== itemRow.defaultRetailPricing.frequency ||
      itemRow.retailPricing.currency !== itemRow.defaultRetailPricing.currency
    ) {
      itemRow.displayOriginalPrice = true;
    }
    return itemRow;
  }

  refreshItemRows(): void {
    this.refreshTrigger$$.next();
  }

  private mapify(array: any[], keyFn: (item: any) => string): Map<string, any> {
    return array.reduce((map, item) => {
      map.set(keyFn(item), item);
      return map;
    }, new Map<string, any>());
  }
}

function AppToCacheKey(app: App): string {
  return createCacheKey(app.key.appId, app.key.editionId);
}

function InventoryItemToCacheKey(item: InventoryItemWithPricing): string {
  return createCacheKey(item.itemId, item.type === 'app' ? item.edition?.editionId : undefined);
}

function createCacheKey(itemId: string, editionId?: string): string {
  if (editionId) {
    return `${itemId}:${editionId}`;
  }
  return itemId;
}
